import styled from '@emotion/styled';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Slider } from '@/components/common/SliderCommon/Slider';
import { determineDestination } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { getChildBackgroundColourToken } from '@/lib/colourUtils';
import { blocksToText } from '@/lib/sanityUtils';
import { blockUrlWithAudience } from '@/lib/utils';

import type { BackgroundColourTokenType } from '@/types/colours';
import type {
  SanityAudienceType,
  SanityImageAsset,
  SanityImageCrop,
  SanityImageHotspot,
  SanityKeyed,
  SanityPressType,
  SanityReference,
} from '@/types/sanity';
import type { BlockWithAudience, TrackingTagType } from '@/types/shared';

export interface PressItemProps {
  _type: 'pressItem';
  logo?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
    crop?: SanityImageCrop;
    hotspot?: SanityImageHotspot;
    alternateText: string;
  };
  tiveImage?: boolean;
  description?: string;
  accessibleLabel: string;
  isDecorativeImage?: boolean;
  audienceRef?: SanityAudienceType;
  noAudienceToggle?: boolean;
  link?: string;
  trackingTag?: TrackingTagType;
}

export interface PressProps
  extends Omit<SanityPressType, 'pressItems'>,
    BlockWithAudience {
  pressItems?: Array<SanityKeyed<PressItemProps>>;
}

interface PressContainerWrapperProps extends BlockWithAudience {
  pressItem: PressItemProps;
  parentBackgroundColourToken: BackgroundColourTokenType;
}

/**
When swapping out a logo, make sure it's going to be a similar size to the other logos. Download one of them to compare. You might have to add some white space to balance it out.
*/

const PressContainerWrapper = ({
  pressItem,
  parentBackgroundColourToken,
  pageAudience,
  isAudienceSwitcherEnabled,
}: PressContainerWrapperProps) => {
  const {
    accessibleLabel,
    description,
    isDecorativeImage,
    link,
    logo,
    trackingTag,
    audienceRef,
    noAudienceToggle,
  } = pressItem;
  const destination = determineDestination(link);

  const childBackgroundColourToken = getChildBackgroundColourToken(
    parentBackgroundColourToken,
    '--background-warm-base',
  );
  return (
    <PressWrapper backgroundColourToken={childBackgroundColourToken}>
      {link ? (
        <BaseLink
          href={blockUrlWithAudience(
            link,
            audienceRef,
            pageAudience,
            noAudienceToggle,
            isAudienceSwitcherEnabled,
          )}
          target={destination.target}
          rel={
            destination.isExternalLink ? 'external noopener noreferrer' : null
          }
          aria-label={
            accessibleLabel
              ? `${accessibleLabel} ${destination.newTabText}`
              : null
          }
          trackingTag={trackingTag}
          textColour="--text-strong"
        >
          <PressContainer aria-hidden="true">
            <LogoWrapper>
              {!!logo && (
                <Image
                  {...logo}
                  isDecorativeImage={isDecorativeImage}
                  sizes="340px"
                />
              )}
            </LogoWrapper>
            {!!description && (
              <PressDescriptionContainer>
                <Paragraph size="subheading-sm">{description}</Paragraph>
              </PressDescriptionContainer>
            )}
          </PressContainer>
        </BaseLink>
      ) : (
        <PressContainer aria-label={accessibleLabel}>
          <LogoWrapper aria-hidden="true">
            {!!logo && (
              <Image
                {...logo}
                isDecorativeImage={isDecorativeImage}
                sizes="340px"
              />
            )}
          </LogoWrapper>
          {!!description && (
            <PressDescriptionContainer aria-hidden="true">
              <Paragraph size="subheading-sm">{description}</Paragraph>
            </PressDescriptionContainer>
          )}
        </PressContainer>
      )}
    </PressWrapper>
  );
};

const Press = ({
  heading,
  caption,
  pressItems,
  backgroundColour,
  pageAudience,
  isAudienceSwitcherEnabled,
}: PressProps) => {
  const headingPlainText = blocksToText(heading);
  const sliderSettings = {
    centerMode: true,
    dots: true,
    infinite: false,
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 1,
    initialSlide: 1,
    arrows: false,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 960,
        settings: 'slick',
      },
      {
        breakpoint: 99999,
        settings: 'unslick',
      },
    ],
  };
  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';
  return (
    <Section
      backgroundColour={backgroundColourToken}
      verticalPadding
      className="press"
    >
      <HeadingLevel>
        {!!heading && (
          <CaptionHeadingDescription
            caption={caption}
            heading={heading}
            desktopAlignment="center"
            mobileAlignment="center"
          />
        )}
        <CarouselContainer>
          <StyledSlider
            settings={sliderSettings}
            heading={headingPlainText}
            slideItems={pressItems}
            slideItemTitleKey="description"
          >
            {pressItems?.map((pressItem) => (
              <PressContainerWrapper
                key={pressItem._key}
                pressItem={pressItem}
                parentBackgroundColourToken={backgroundColourToken}
                pageAudience={pageAudience}
                isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
              />
            ))}
          </StyledSlider>
        </CarouselContainer>
      </HeadingLevel>
    </Section>
  );
};

const StyledSlider = styled(Slider)`
  padding: var(--spacing-300) 0 var(--spacing-500);
  gap: var(--spacing-large);
  .slick-slide {
    margin: 0 calc(var(--spacing-large) / 2);
    height: unset;
    display: flex !important;
  }
  .slick-list {
    margin: 0;
  }
  .slick-track {
    display: flex;
    margin: 0 calc(0px - var(--spacing-large)) var(--spacing-x-large);
  }
  .slick-slide div {
    outline: none;
  }
  ${screen.sm} {
    display: flex;
    padding: 0;
    justify-content: space-around;
    align-items: stretch;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
    user-select: auto;

    > div {
      flex: 1 0;
    }
  }
`;
const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;
`;
const PressWrapper = styled.div<{ backgroundColourToken }>`
  display: flex;
  justify-content: center;
  height: 100%;
  ${screen.md} {
    height: unset;
  }
  a {
    text-decoration: none;
    color: inherit;
  }
  background: ${({ backgroundColourToken }) => `var(${backgroundColourToken})`};
  border-radius: var(--radius-xl);
`;
const PressContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  padding: var(--spacing-x-large);
  max-width: 249px;
  ${screen.md} {
    max-width: 325px;
  }
`;
const PressDescriptionContainer = styled.div`
  padding-top: var(--spacing-medium);
  max-width: 250px;
  text-align: center;

  align-self: center;
  white-space: pre-line;
  a {
    text-decoration: none;
    color: inherit;

    &:hover {
      opacity: 0.5;
    }
  }

  ${screen.md} {
    max-width: 288px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
`;

export { Press };
